import { useState } from 'react'

/**
 * Primeros pasos que debe seguir quien reporta un incidente (generados con IA
 * o, si no hay IA, los básicos del tipo). La persona puede ir marcándolos.
 *
 * @component
 * @param {Object} props
 * @param {{summary: string, steps: Array<{title: string, detail: string, urgent: boolean}>,
 *   dont: string[], source: 'ai'|'fallback'}|null} props.guide
 * @param {boolean} props.loading
 * @param {string|null} [props.aiError] - Motivo si se usaron los pasos básicos.
 * @param {() => void} [props.onRetry] - Reintentar con IA.
 * @returns {JSX.Element}
 */
const IncidentFirstSteps = ({ guide, loading, aiError, onRetry }) => {
  const [done, setDone] = useState([])

  const toggle = (index) =>
    setDone((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )

  if (loading) {
    return (
      <div className="ifs-loading">
        <span className="spinner-border spinner-border-sm text-primary" />
        <span>Preparando los pasos a seguir…</span>
      </div>
    )
  }

  if (!guide) return null

  return (
    <div className="ifs">
      <div className="ifs-summary">
        <span className="ifs-icon">
          <i
            className={`fa-solid ${guide.source === 'ai' ? 'fa-wand-magic-sparkles' : 'fa-shield-halved'}`}
          />
        </span>
        <p className="mb-0">{guide.summary}</p>
      </div>

      <ol className="ifs-steps">
        {guide.steps.map((step, i) => {
          const checked = done.includes(i)
          return (
            <li key={step.title} className={checked ? 'is-done' : ''}>
              <label>
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={checked}
                  onChange={() => toggle(i)}
                  aria-label={`Marcar como hecho: ${step.title}`}
                />
                <span className="ifs-step-body">
                  <span className="ifs-step-title">
                    {step.title}
                    {step.urgent && <span className="ifs-urgent">Ahora</span>}
                  </span>
                  <span className="ifs-step-detail">{step.detail}</span>
                </span>
              </label>
            </li>
          )
        })}
      </ol>

      {guide.dont?.length > 0 && (
        <div className="ifs-dont">
          <span className="ifs-dont-title">
            <i className="fa-solid fa-ban" /> No haga esto
          </span>
          <ul>
            {guide.dont.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="ifs-foot">
        <span>
          {guide.source === 'ai'
            ? 'Guía generada con IA a partir de su reporte. Ante la duda, llame a TI.'
            : `Pasos básicos para este tipo de incidente${aiError ? ' (la IA no está disponible en este momento)' : ''}.`}
        </span>
        {guide.source !== 'ai' && onRetry && (
          <button type="button" className="ifs-retry" onClick={onRetry}>
            <i className="fa-solid fa-rotate-right me-1" />
            Reintentar con IA
          </button>
        )}
      </div>

      <style>{`
        .ifs-loading { display: flex; align-items: center; gap: 10px; padding: 24px 4px; font-size: 0.85rem; color: var(--gm-text-muted); }
        .ifs { display: flex; flex-direction: column; gap: 12px; }
        .ifs-summary { display: flex; gap: 10px; align-items: flex-start; padding: 12px 14px; border-radius: 12px; background: var(--gm-primary-soft); font-size: 0.88rem; color: var(--gm-text); }
        .ifs-icon { flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; background: linear-gradient(135deg, var(--brand), var(--accent)); font-size: 0.75rem; }
        .ifs-steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; counter-reset: ifs; }
        .ifs-steps li { border: 1px solid var(--gm-border); border-radius: 10px; background: var(--gm-surface); transition: opacity 0.15s ease; }
        .ifs-steps li.is-done { opacity: 0.55; }
        .ifs-steps li.is-done .ifs-step-title { text-decoration: line-through; }
        .ifs-steps label { display: flex; gap: 10px; align-items: flex-start; margin: 0; padding: 10px 12px; cursor: pointer; }
        .ifs-steps input { margin-top: 3px !important; flex-shrink: 0; }
        .ifs-step-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .ifs-step-title { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 0.86rem; color: var(--gm-text); }
        .ifs-urgent { font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 1px 7px; border-radius: 99px; color: var(--gm-danger); background: rgba(220, 53, 69, 0.12); }
        .ifs-step-detail { font-size: 0.8rem; color: var(--gm-text-muted); }
        .ifs-dont { padding: 10px 14px; border-radius: 10px; background: rgba(220, 53, 69, 0.07); border: 1px solid rgba(220, 53, 69, 0.22); }
        .ifs-dont-title { display: block; margin-bottom: 4px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--gm-danger); }
        .ifs-dont ul { margin: 0; padding-left: 1.1rem; font-size: 0.8rem; color: var(--gm-text); }
        .ifs-foot { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 8px; font-size: 0.72rem; color: var(--gm-text-faint); }
        .ifs-retry { border: none; background: transparent; padding: 0; color: var(--gm-primary); font-size: 0.75rem; font-weight: 600; }
      `}</style>
    </div>
  )
}

export default IncidentFirstSteps
